import { useEffect, useRef, useState, useCallback } from "react";
import QRCode from "qrcode";
import { LetterHeader, LetterBody, footerLines } from "@/components/letters/LetterDocument";
import { renderPages, pagesToPdf, printPages } from "@/components/letters/render";

// Monte le document hors écran et expose generate() -> pages (images A4)
export function useLetterOutput({ letter, cfg, company }) {
  const head = useRef(null);
  const body = useRef(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState("");
  const code = letter?.esign?.code;
  useEffect(() => {
    if (!code) { setQr(""); return; }
    QRCode.toDataURL(`${window.location.origin}/verifier/${code}`, { margin: 1, width: 180, color: { dark: "#0f172a" } }).then(setQr);
  }, [code]);
  const signatory = (cfg?.signatories || []).find((s) => s.id === letter?.signataire_id);
  const generate = useCallback(async () => {
    setBusy(true);
    try { return await renderPages(head.current, body.current, footerLines(company || {})); } finally { setBusy(false); }
  }, [company]);
  const name = `courrier-${(letter?.reference || "brouillon").replace(/[^A-Za-z0-9-]+/g, "-")}`;
  const pdf = async () => pagesToPdf(await generate());
  const actions = {
    download: async () => (await pdf()).save(`${name}.pdf`),
    print: async () => printPages(await generate(), name),
    base64: async () => (await pdf()).output("datauristring").split(",")[1],
  };
  const node = letter && cfg && company && (!code || qr) ? (
    <div aria-hidden style={{ position: "fixed", left: -12000, top: 0, pointerEvents: "none" }}>
      <div ref={head}><LetterHeader company={company} /></div>
      <div ref={body}><LetterBody letter={letter} cfg={cfg} signatory={signatory} company={company} qr={qr} /></div>
    </div>
  ) : null;
  return { node, generate, busy, ...actions };
}

export function PagesView({ pages }) {
  return (
    <div className="space-y-4" data-testid="letter-pages">
      {pages.map((p, i) => <img key={i} src={p} alt={`Page ${i + 1}`} className="mx-auto w-full max-w-[794px] shadow-lg bg-white" data-testid={`letter-page-${i + 1}`} />)}
    </div>
  );
}

export function LivePreview({ output, deps }) {
  const [pages, setPages] = useState(null);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => output.generate().then((p) => alive && setPages(p)).catch(() => alive && setPages([])), 250);
    return () => { alive = false; clearTimeout(t); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pages) return <p className="text-sm text-slate-500 p-6 text-center">Mise en page du document…</p>;
  return <PagesView pages={pages} />;
}
